import { useState, useCallback, useRef, useEffect } from "react";
import useSWR from "swr";
import axios from "axios";
import BigJSON from "./json";

axios.defaults.transformResponse = [
  (data) => {
    if (BigJSON.isJSON(data)) {
      try {
        return BigJSON.parse(data);
      } catch (_) {
        return {};
      }
    }
    return data;
  },
];

export type ApiMethod = "GET" | "POST" | string;
export type HostDomain = string;
export interface RequestOptions {
  headers?: any;
  params?: any;
  extra?: any;
  safety?: boolean;
}
export interface ResponseData {
  data: any;
  isLoading: boolean;
  error: any;
  errorCode?: number;
  extra?: any;
}

/**
 * useGet auto call api
 * @param apiPath api-path e.g. "/get_info/?flag=123"
 * @param domain api-domain e.g. "https://gmnetwork.ai/"
 * @param options options.domain base-api
 * @param functions functions
 */
export function useGet({
  apiPath,
  domain,
  options,
  functions,
}: {
  apiPath: string;
  domain: HostDomain;
  options?: {
    headers?: any;
    configs?: { [key in string]: any };
  };
  functions?: { onResponse?: (data: any, error: any) => void };
}): ResponseData {
  const apiUrl = _getUrl(apiPath, domain);

  const { data, error, isLoading } = useSWR(
    { apiUrl: apiUrl, headers: options?.headers },
    _fetcher,
    options?.configs
  );

  const _error = data?.error || error;
  const _data = data?.data;

  if (functions?.onResponse != null) {
    functions?.onResponse(_data, _error);
  }

  const isSuccess = !!_data;
  return {
    isLoading: isLoading || (!_error && !_data),
    data: isSuccess ? _data : undefined,
    error: !isSuccess ? _error : undefined,
    errorCode: _error?.error_code || _error?.code || 0,
  };
}

/**
 * useFetch hooks-response
 * @param method only "GET" | "POST"
 * @param apiPath api-path e.g. "/get_info/"
 * @param domain api-domain e.g. "https://gmnetwork.ai/"
 * @param options options.params request params
 * @param functions functions
 */
export function useFetch({
  method,
  apiPath,
  domain,
  options,
  functions,
}: {
  method: ApiMethod;
  apiPath: string;
  domain: HostDomain;
  options?: RequestOptions;
  functions?: RequestFunctions;
}): [
  ResponseData,
  (
    params?: any,
    others?: RequestOptions & { domain?: HostDomain | any }
  ) => void
] {
  const [rsp, setRsp] = useState<ResponseData>({
    data: null,
    error: null,
    isLoading: false,
    extra: {},
  });

  const reqRef = useRef({
    method,
    apiPath,
    domain,
    params: options?.params,
    headers: options?.headers,
    extra: options?.extra,
    safety: options?.safety,
  });
  useEffect(() => {
    reqRef.current = {
      method,
      apiPath,
      domain,
      params: options?.params,
      headers: options?.headers,
      extra: options?.extra,
      safety: options?.safety,
    };
  }, [method, apiPath, domain]);

  const functionsRef = useRef(functions);
  useEffect(() => {
    functionsRef.current = functions;
    return () => {
      functionsRef.current = {};
    };
  }, [functions]);

  const callAPI = useCallback(
    (
      params?: any,
      others?: RequestOptions & { domain?: HostDomain | any }
    ): void => {
      const reqOptions = reqRef.current;
      const reqFunctions = functionsRef.current;

      setRsp((prev) => ({
        ...prev,
        isLoading: true,
        extra: { ...reqOptions?.extra, ...others?.extra },
      }));

      const safety = others?.safety || reqOptions?.safety || false;
      const domain =
        reqFunctions?.resolveDomain != null && !!others?.domain
          ? reqFunctions?.resolveDomain(others?.domain)
          : others?.domain || reqOptions?.domain || "";
      request({
        method: reqOptions.method,
        apiPath: reqOptions.apiPath,
        domain: domain,
        options: {
          params: { ...reqOptions?.params, ...params },
          headers: {
            ...reqOptions?.headers,
            ...others?.headers,
            ...reqFunctions?.getAccessHeaders?.(),
          },
          safety: safety,
        },
        functions: {
          getSafetyToken: reqFunctions?.getSafetyToken,
        },
      })
        .then((res) => {
          if (reqFunctions?.onResponse != null) {
            reqFunctions?.onResponse(res.data, res.error, {
              ...options,
              ...others,
            });
          }
          if (!!res.data) {
            setRsp((prev) => ({
              ...prev,
              data: res.data,
              isLoading: false,
              error: null,
            }));
          } else {
            setRsp((prev) => ({
              ...prev,
              data: null,
              isLoading: false,
              error: res.error,
              errorCode: res.error.error_code || res.error.code,
            }));
          }
        })
        .catch((error) => {
          setRsp((prev) => ({
            ...prev,
            data: null,
            isLoading: false,
            error: {
              ...error,
              message: error?.message || _request_error,
            },
            errorCode: error.error_code || error.code,
          }));
        });
    },
    []
  );

  return [rsp, callAPI];
}

/**
 * useRequest promise-function-response
 * @param method only "GET" | "POST"
 * @param apiPath api-path e.g. "/get_info/"
 * @param domain api-domain e.g. "https://gmnetwork.ai/"
 * @param options options.params request params
 * @param functions functions
 */
export function useRequest({
  method,
  apiPath,
  domain,
  options,
  functions,
}: {
  method?: ApiMethod;
  apiPath?: string;
  domain?: HostDomain;
  options?: RequestOptions;
  functions?: RequestFunctions;
}): (
  params?: any,
  others?: RequestOptions & {
    method?: ApiMethod;
    apiPath?: string;
    domain?: HostDomain | any;
  }
) => Promise<{ data: any; error: any }> {
  const functionsRef = useRef(functions);
  useEffect(() => {
    functionsRef.current = functions;
    return () => {
      functionsRef.current = {};
    };
  }, [functions]);

  return useCallback(
    async (
      params?: any,
      others?: RequestOptions & {
        method?: ApiMethod;
        apiPath?: string;
        domain?: HostDomain | any;
      }
    ): Promise<{
      data: any;
      error: any;
    }> => {
      const reqFunctions = functionsRef.current;

      const _apiMethod = others?.method || method;
      const _apiPath = others?.apiPath || apiPath;
      const _apiDomain =
        reqFunctions?.resolveDomain != null && !!others?.domain
          ? reqFunctions?.resolveDomain(others?.domain)
          : others?.domain || domain || "";
      if (!_apiMethod || !_apiPath || !_apiDomain) {
        return { data: null, error: _parameters_error };
      }

      const safety = others?.safety || options?.safety || false;
      const res = await request({
        method: _apiMethod!,
        apiPath: _apiPath!,
        domain: _apiDomain,
        options: {
          params: { ...options?.params, ...params },
          headers: {
            ...options?.headers,
            ...others?.headers,
            ...reqFunctions?.getAccessHeaders?.(),
          },
          safety: safety,
        },
        functions: {
          getSafetyToken: reqFunctions?.getSafetyToken,
        },
      });

      if (reqFunctions?.onResponse != null) {
        reqFunctions?.onResponse(res.data, res.error, {
          ...options,
          ...others,
        });
      }

      return res;
    },
    []
  );
}

/**
 * request promise-response
 * @param method only "GET" | "POST"
 * @param apiPath api-path e.g. "/get_info/"
 * @param domain api-domain e.g. "https://gmnetwork.ai/"
 * @param options options.params request params
 * @param functions functions
 */
export async function request({
  method,
  apiPath,
  domain,
  options,
  functions,
}: {
  method: ApiMethod;
  apiPath: string;
  domain: HostDomain;
  options?: {
    params?: any;
    headers?: any;
    safety?: boolean;
  };
  functions?: {
    getSafetyToken?: () => Promise<string>;
  };
}): Promise<{ data: any; error: any }> {
  const apiUrl = _getUrl(apiPath, domain);
  if (!apiUrl) {
    return { data: null, error: _parameters_error };
  }

  try {
    let safetyHeaders: any = {};
    if (!!options?.safety) {
      if (functions?.getSafetyToken == null) {
        return { data: null, error: _parameters_error };
      }

      const turnstileToken = await functions?.getSafetyToken();
      if (!turnstileToken) {
        return { data: null, error: _safety_error };
      }
      safetyHeaders = { "Cf-Turnstile-Resp": turnstileToken };
    }

    const fetchHeaders = { ...options?.headers, ...safetyHeaders };
    const fetchData = { ...options?.params };
    const isGet = method.toUpperCase() === "GET";

    const res = isGet
      ? await axios.get(apiUrl, {
          params: fetchData,
          headers: fetchHeaders,
        })
      : await axios.post(apiUrl, fetchData, { headers: fetchHeaders });

    return { data: res.data, error: null };
  } catch (error: any) {
    const _error = error.response?.data || error || {};
    return {
      data: null,
      error: {
        ..._error,
        message: _error.error_message || _error.message || _request_error,
      },
    };
  }
}

function _getUrl(path?: string | null, domain?: HostDomain): string {
  if (!path) return "";
  if (path.startsWith("http")) return path;

  const host: string = !!domain && domain.startsWith("http") ? domain : "";
  if (!host) return "";

  const space = !host.endsWith("/") && !path.startsWith("/") ? "/" : "";
  return `${host}${space}${path}`;
}

async function _fetcher({
  apiUrl,
  headers,
}: {
  apiUrl: string;
  headers?: any;
}): Promise<{ error: any; data: any }> {
  if (!apiUrl) {
    return {
      data: null,
      error: _parameters_error,
    };
  }

  try {
    const res = await axios.get(apiUrl, { headers: headers || {} });
    return { data: res.data, error: null };
  } catch (error: any) {
    const _error = error.response?.data || error || {};
    return {
      data: null,
      error: {
        ..._error,
        message: _error.error_message || _error.message || _request_error,
      },
    };
  }
}

interface RequestFunctions {
  onResponse?: (data: any, error: any, requestOptions?: any) => void;
  getSafetyToken?: () => Promise<string>;
  getAccessHeaders?: () => any;
  resolveDomain?: (domain: any) => string;
}

enum _ErrorCodes {
  invalidHuman = -2000,
  invalidParameters = -2001,
}

const _request_error = "Request error, please try again later";
const _parameters_error = {
  name: "Parameters Error",
  message: "Invalid request parameters",
  code: _ErrorCodes.invalidParameters,
};
const _safety_error = {
  name: "Verification Failed",
  message:
    "Please try again. Make sure to complete the verification correctly.",
  code: _ErrorCodes.invalidHuman,
};
